import React from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Facebook from '@mui/icons-material/Facebook';
import YouTube from '@mui/icons-material/YouTube';
import { useTranslation } from 'react-i18next';
import 'react-quill-new/dist/quill.snow.css';

interface SystemNotificationDialogProps {
    open: boolean;
    data: any;
    onClose: () => void;
}

const SystemNotificationDialog: React.FC<SystemNotificationDialogProps> = ({ open, data, onClose }) => {
    const { t } = useTranslation();

    if (!data) return null;

    const handleAction = () => {
        if (data.target_url) {
            if (['class_gallery', 'class_post_pending', 'class_post_public', 'class_post_reviewed'].includes(data.type)) {
                const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://trogiang.online';
                window.open(marketingUrl + data.target_url, '_blank');
            } else {
                window.location.href = data.target_url;
            }
        }
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth={false}
            scroll="body"
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        backdropFilter: 'blur(10px)'
                    }
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    width: '100%',
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '80vw', md: '50vw' },
                    m: { xs: 2, sm: 3 },
                    p: 0,
                    background: '#ffffff',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                <img src="/logo.png" alt="Logo" style={{ height: 32, objectFit: 'contain' }} />
                <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'primary.main', letterSpacing: 1.5, fontSize: '0.85rem', lineHeight: 1, mt: 0.5 }}>
                    {t('notifications.systemNotice', 'THÔNG BÁO HỆ THỐNG')}
                </Typography>
                <IconButton 
                    onClick={onClose} 
                    sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 3, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="800" sx={{ mt: 2, mb: 2, color: '#1A2027', textAlign: 'center' }}>
                    {data.title || 'Thông báo từ Trợ giảng AI'}
                </Typography>
                
                <Box 
                    className="ql-snow"
                    sx={{ 
                        typography: 'body1', 
                        color: '#4A5568', 
                        mb: 4,
                        textAlign: 'left',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        '& *': {
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                        },
                        '& img': { 
                            maxWidth: '100%', 
                            borderRadius: 2, 
                            mt: 2, 
                            display: 'block', 
                            mx: 'auto',
                            height: 'auto'
                        }
                    }}
                >
                    <div 
                        className="ql-editor content-viewer"
                        style={{ padding: 0 }}
                        dangerouslySetInnerHTML={{ __html: data.content }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined" 
                        fullWidth 
                        onClick={onClose}
                        sx={{ 
                            py: 1.5, 
                            color: 'text.secondary',
                            fontWeight: 'bold',
                            borderRadius: 2,
                            borderColor: 'rgba(0, 0, 0, 0.1)',
                            borderWidth: 2,
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': { 
                                borderColor: 'rgba(0, 0, 0, 0.3)', 
                                bgcolor: 'rgba(0, 0, 0, 0.02)',
                                borderWidth: 2
                            }
                        }}
                    >
                        {t('common.close', 'Đóng')}
                    </Button>
                    {data.target_url && (
                        <Button 
                            variant="contained" 
                            fullWidth 
                            onClick={handleAction}
                            sx={{ 
                                py: 1.5, 
                                bgcolor: '#1942d8', 
                                fontWeight: 'bold',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: '0 4px 12px rgba(25, 66, 216, 0.3)',
                                '&:hover': { bgcolor: '#1536b1' }
                            }}
                        >
                            Khám phá ngay
                        </Button>
                    )}
                </Box>
            </DialogContent>
            
            <Box sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.6)', 
                py: 2, 
                textAlign: 'center',
                borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
            }}>
                <Typography variant="overline" sx={{ fontWeight: 'bold', color: '#666', letterSpacing: 1.5 }}>
                    KẾT NỐI VỚI TRỢ GIẢNG
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <IconButton 
                        component="a" 
                        href="https://www.facebook.com/trogiangonline" 
                        target="_blank"
                        sx={{ color: '#1877F2', bgcolor: 'rgba(24, 119, 242, 0.1)', '&:hover': { bgcolor: 'rgba(24, 119, 242, 0.2)' } }}
                    >
                        <Facebook />
                    </IconButton>
                    <IconButton 
                        component="a" 
                        href="https://www.youtube.com/@tgotrogiangonline" 
                        target="_blank"
                        sx={{ color: '#FF0000', bgcolor: 'rgba(255, 0, 0, 0.1)', '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.2)' } }}
                    >
                        <YouTube />
                    </IconButton>
                </Box>
            </Box>
        </Dialog>
    );
};

export default SystemNotificationDialog;
